import { Box, Spinner, Stack } from '@chakra-ui/react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import OpenGraph from '@/components/OpenGraph';
import FullPage from '@/src/components/UI/FullPage';
import Listings from '@/src/components/UI/Listings';
import React, { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import FilterContainer from '../../src/components/UI/FilterContainer';
import {
  FilterListingContextProvider,
  ListingSortingType,
  useFilterListingContextProvider,
} from '@/src/context/FilterListingContext';
import {
  convertListingSortingType,
  ListingQueryResultContextProvider,
  useListingQueryResultContextProvider,
} from '@/src/context/ListingQueryResultContext';
import FilterEmpty from '../../src/components/UI/FilterEmpty';
import { GetServerSideProps } from 'next';
import ConditionalFragment from '@/components/common/ConditionalFragment';
import { isEmptyArray } from '@/utils/formatChecker';
import FunwooAPI, { CountryEnum, GetOptionsForBuy, ListingListEntity, SearchListingDto } from '@/swagger';
import { searchParamParser } from '@/utils/searchParamParser';
import { BsArrowUpShort } from 'react-icons/bs';
import { findScrollableParent } from '@/utils/findScrollElement';
import chunk from 'lodash/chunk';
import ReactIcon from '@/src/components/common/ReactIcon';

// const DynamicBuyHouseConsultantForm = dynamic<ComponentProps<typeof BuyHouseConsultantForm>>(
//   () => import('@/src/components/feature/ConsultantForm/ConsultantForm').then((mod) => mod.BuyHouseConsultantForm),
//   {
//     ssr: false,
//   }
// );

interface Props extends GetOptionsForBuy {
  listings: Array<Array<ListingListEntity | { sid: 'marketing' }>>;
}

export interface PositionProps {
  cities: string[];
  country: CountryEnum | 'ALL';
}

const BuyPageContainer: React.FC<Props> = ({ buildingTypesData, citiesData, usCitiesData }) => {
  const { listingResult, loading } = useListingQueryResultContextProvider();
  const { resultNum } = useFilterListingContextProvider();

  const mainContainerRef = useRef<HTMLDivElement | null>(null);

  return (
    <React.Fragment>
      <BackToTop mainContainerRef={mainContainerRef} />
      <FullPage ref={mainContainerRef} hasPadding hasBottomMargin={false} hasHeaderPlaceHolder>
        <OpenGraph title='FUNWOO 德載國際不動產 - 買屋' type='funwoo.buy' url='https://funwoo.com.tw/buy' />
        <FilterContainer
          citiesData={citiesData}
          usCitiesData={usCitiesData}
          resultNum={resultNum}
          buildingTypesData={buildingTypesData}
        />
        <ConditionalFragment condition={isEmptyArray(listingResult)}>
          <FilterEmpty />
        </ConditionalFragment>
        {listingResult
          ?.map((group) => (
            <Box key={group[0].sid} id={group[0].sid} className={'mb-8'}>
              <Listings data={group} />
            </Box>
          ))
          .flat()}
        <Stack w={'full'} align={'center'} justify={'center'}>
          {loading ? <Spinner alignSelf={'center'} /> : null}
        </Stack>
        {/*<DynamicBuyHouseConsultantForm />*/}
      </FullPage>
    </React.Fragment>
  );
};

const BuyPage: FC<Props> = ({ listings, buildingTypesData, citiesData, usCitiesData }) => {
  return (
    <FilterListingContextProvider>
      <ListingQueryResultContextProvider defaultListing={listings}>
        <BuyPageContainer
          listings={listings}
          buildingTypesData={buildingTypesData}
          citiesData={citiesData}
          usCitiesData={usCitiesData}
        />
      </ListingQueryResultContextProvider>
    </FilterListingContextProvider>
  );
};
export default BuyPage;

export interface BuyPageSearchParams
  extends Partial<Omit<SearchListingDto, 'paging' | 'deprioritizeSoldItem' | 'sorting'>> {
  sortBy?: ListingSortingType;
}

export const getServerSideProps: GetServerSideProps<Props, { country?: CountryEnum | Array<CountryEnum> }> = async ({
  locale = '',
  query,
}) => {
  let {
    country = [],
    cities = [],
    buildingType = [],
    sortBy,
    listingStatus,
  } = query as ReplaceValueType<BuyPageSearchParams, Array<string>, string | Array<string>>;
  let sorting: SearchListingDto['sorting'] = convertListingSortingType(sortBy);

  if (!Array.isArray(cities)) {
    cities = [cities];
  }
  if (!Array.isArray(buildingType)) {
    buildingType = [buildingType];
  }

  const [
    {
      data: { payload = [] },
    },
    {
      data: { buildingTypesData, citiesData, usCitiesData },
    },
  ] = await Promise.all([
    FunwooAPI.listingApi.searchList({
      paging: { page: 1, pageSize: 12 },
      country: searchParamParser(country as CountryEnum | Array<CountryEnum>),
      cities,
      buildingType,
      sorting,
      listingStatus,
    }),
    FunwooAPI.optionApi.getOptionsForBuy(),
  ]);

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      listings: chunk(payload, 12),
      buildingTypesData,
      citiesData,
      usCitiesData,
    },
  };
};

const BackToTop: React.FC<{ mainContainerRef: RefObject<HTMLDivElement | null> }> = ({ mainContainerRef }) => {
  const [offsetTop, setOffsetTop] = useState<number>(0);
  const [topest, setTopest] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const topest = findScrollableParent(mainContainerRef.current);
    if (!topest) return;
    setTopest(topest);
  }, []);

  useEffect(() => {
    if (!topest) return;

    function onScroll(this: HTMLDivElement, _: HTMLElementEventMap['scroll']) {
      const scrollTop = this.scrollTop;
      setOffsetTop(scrollTop);
    }

    topest?.addEventListener('scroll', onScroll);

    return () => {
      topest?.removeEventListener('scroll', onScroll);
    };
  }, [topest]);

  const scrollToTop = useCallback(() => {
    if (!topest) return;
    topest?.scrollTo(0, 0);
  }, [topest]);

  if (offsetTop <= 0) return null;

  return (
    <button
      onClick={scrollToTop}
      type='button'
      className={
        'fixed right-4 md:bottom-4 bottom-16 z-[100] flex items-center justify-center w-12 h-12 bg-black text-white'
      }
    >
      <ReactIcon icon={BsArrowUpShort} size={24} />
    </button>
  );
};
